import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Minus, Plus } from "lucide-react";
import { billingPlans, billingPurchaseAddons, billingCancelAddon, errorMessage } from "./api";
import { money, per } from "./pricing";
import { card } from "./ui";

// Profile → Add-ons. Extras follow the plan's billing (monthly plan: monthly extras; yearly plan: yearly extras). Changes are collected in a
// summary at the bottom and paid in one tap with the card already on file. Every price and limit comes from the gateway.
const day = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
const btn = "inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-[10px] px-4 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 disabled:opacity-50";

function Stepper({ value, onMinus, onPlus, minusOff, plusOff, label }) {
  return (
    <div className="inline-flex items-center gap-1" role="group" aria-label={label}>
      <button type="button" className="grid h-11 w-11 place-items-center rounded-[10px] border border-sand-300 text-forest-700 hover:bg-sand-100 disabled:opacity-40" disabled={minusOff} onClick={onMinus} aria-label={`Fewer ${label}`}><Minus className="h-4 w-4" aria-hidden="true" /></button>
      <span className="w-10 text-center font-display text-lg font-semibold" aria-live="polite">{value}</span>
      <button type="button" className="grid h-11 w-11 place-items-center rounded-[10px] border border-sand-300 text-forest-700 hover:bg-sand-100 disabled:opacity-40" disabled={plusOff} onClick={onPlus} aria-label={`More ${label}`}><Plus className="h-4 w-4" aria-hidden="true" /></button>
    </div>
  );
}

export default function AddonsTab({ token, onChanged, goPlans }) {
  const [d, setD] = useState(null);
  const [want, setWant] = useState({});            // irtaqi / living: true when picked
  const [stories, setStories] = useState({});      // member_id -> extra stories picked (steps of 10)
  const [packs, setPacks] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const load = useCallback(() => billingPlans(token).then(setD).catch((e) => toast.error(errorMessage(e))), [token]);
  useEffect(() => { load(); }, [load]);

  const view = useMemo(() => {
    if (!d || d.managed_by_school) return null;
    const by = (id) => d.addons.find((a) => a.addon_id === id);
    const active = (id) => d.my_addons.filter((m) => m.addon_id === id).sort((a, b) => (a.expires_at < b.expires_at ? 1 : -1))[0];
    return { by, active, irtaqi: by("irtaqi"), living: by("living"), top: by("story_topup"), ai: by("ai_tests") };
  }, [d]);
  if (!d) return <div className="text-forest-700">Loading…</div>;
  if (!view) return <div className={card}>Your access is managed by your school, so there are no add-ons to buy here.</div>;

  const { irtaqi, living, top, ai, active } = view;
  const paid = d.current_plan_id !== "free";
  const plan = d.plans.find((p) => p.current);
  const sub = d.subscription;
  const interval = d.plan_interval;

  if (!paid) {
    return <div className={`${card} space-y-3`} data-testid="addons-nopaid"><h2 className="font-display text-xl font-semibold text-forest-700">Add-ons come with a plan</h2>
      <p className="text-sm text-foreground/70">Choose a plan first, then add Irtaqi, Living with the Qur&apos;an, more stories or extra AI tests here, whenever you need them.</p>
      <button type="button" onClick={goPlans} className={`${btn} bg-forest-700 text-sand-100`} data-testid="addons-choose">Choose a plan</button></div>;
  }
  if (!d.can_manage_addons) {
    return <div className={card} data-testid="addons-member">Add-ons are managed by the person who pays for your plan. Ask them to add what you need from their profile.</div>;
  }

  const unit = (id) => (interval === "yearly" ? "yr" : "mo");
  const items = [];
  const lines = [];
  if (want.irtaqi && irtaqi?.price) { items.push({ addon_id: "irtaqi" }); lines.push([irtaqi.name, irtaqi.price, `/ ${unit()}`]); }
  if (want.living && living?.price) { items.push({ addon_id: "living" }); lines.push([living.name, living.price, `/ ${unit()}`]); }
  for (const c of top?.children || []) {
    const n = stories[c.member_id] || 0;
    if (n > 0) { items.push({ addon_id: "story_topup", quantity: n, member_id: c.member_id }); lines.push([`${n} more stories${(top.children.length > 1) ? ` for ${(c.name || "").split(" ")[0]}` : ""}`, n * top.unit_price, "/ yr"]); }
  }
  for (let i = 0; i < packs; i++) { items.push({ addon_id: "ai_tests" }); }
  if (packs > 0) lines.push([`${packs} pack${packs > 1 ? "s" : ""} of ${ai.pack_size} AI tests`, packs * ai.prices.once, "once"]);
  const total = lines.reduce((t, l) => t + l[1], 0);

  const confirm = async () => {
    setBusy(true);
    try {
      await billingPurchaseAddons(token, items);
      toast.success("Added. You'll find it here under Add-ons.");
      setWant({}); setStories({}); setPacks(0); setDone(true);
      await load(); onChanged?.();
    } catch (e) { toast.error(errorMessage(e, "We couldn't add that. Nothing was charged.")); } finally { setBusy(false); }
  };
  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name}? It keeps working until the end of what you've paid for.`)) return;
    try { const r = await billingCancelAddon(token, id); toast.success(`${name} will end on ${day(r.ends_at)}`); await load(); } catch (e) { toast.error(errorMessage(e, "Couldn't remove it")); }
  };

  const renderSub = (a, id) => {
    if (!a) return null;
    const cur = active(id);
    const picked = !!want[id];
    return (
      <section key={id} className={`${card} flex flex-wrap items-center justify-between gap-3`} data-testid={`addon-${id}`}>
        <div className="min-w-[220px] flex-1">
          <div className="flex items-center gap-2"><h3 className="font-semibold text-forest-700">{a.name}</h3>
            {cur && <span className="rounded-full bg-[#DEF5E5] px-2.5 py-0.5 text-[11px] font-bold text-forest-700" data-testid={`active-${id}`}>Active</span>}</div>
          <p className="text-sm text-foreground/70">{a.description}</p>
          {cur && <p className="mt-1 text-xs text-foreground/65">{cur.auto_renew ? `Renews ${day(cur.expires_at)}` : `Ends ${day(cur.expires_at)}; it won't renew`}</p>}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm"><strong className="font-display text-lg text-forest-700">{money(a.price)}</strong> <span className="text-foreground/65">/ {per(a.billing)}</span></span>
          {cur
            ? cur.auto_renew && <button type="button" className={`${btn} border border-sand-300 text-forest-700 hover:bg-sand-100`} onClick={() => remove(id, a.name)} data-testid={`remove-${id}`}>Remove</button>
            : <button type="button" className={`${btn} ${picked ? "bg-forest-700 text-sand-100" : "border border-forest-600/40 text-forest-700 hover:bg-forest-600/5"}`} disabled={!a.available} aria-pressed={picked}
                onClick={() => setWant((w) => ({ ...w, [id]: !w[id] }))} data-testid={`add-${id}`}>{picked ? <><Check className="h-4 w-4" aria-hidden="true" />Added</> : <><Plus className="h-4 w-4" aria-hidden="true" />Add</>}</button>}
        </div>
      </section>
    );
  };

  return (
    <div className="space-y-5 pb-2" data-testid="addons-tab">
      <section className="rounded-2xl bg-forest-700 p-5 text-sand-100" data-testid="plan-summary">
        <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-gold-400">Your plan</div>
        <div className="mt-1 flex flex-wrap items-baseline justify-between gap-2">
          <span className="font-display text-xl font-semibold">{plan?.name || "Your plan"} · {interval}</span>
          {sub && <span className="font-semibold">{money(sub.amount)} / {per(sub.interval)}</span>}
        </div>
        {d.founding.lock && <p className="mt-1 text-sm text-[#D4E9DB]" data-testid="plan-founding">Founding price, locked while you stay subscribed. Renewal reminder 7 days before.</p>}
      </section>
      {done && <p className={`${card} bg-[#DEF5E5] text-sm text-forest-700`} data-testid="addons-done"><strong>Added.</strong> It&apos;s active now and listed below.</p>}

      {renderSub(irtaqi, "irtaqi")}
      {renderSub(living, "living")}

      {top?.available || (top && top.children?.length) ? (
        <section className={`${card} space-y-4`} data-testid="addon-stories">
          <div><h3 className="font-semibold text-forest-700">More stories</h3>
            <p className="text-sm text-foreground/70">{top.included} stories a year are included for each child. Add {top.step} at a time at {money(top.unit_price)} each, up to {top.max_total} a year.</p></div>
          {top.children.map((c) => {
            const n = stories[c.member_id] || 0;
            const total_ = top.included + c.extra + n;
            return (
              <div key={c.member_id} className="space-y-2" data-testid={`child-${c.member_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="font-medium">{c.self && top.children.length === 1 ? "Stories each year" : c.name}</span>
                  <Stepper value={n} label="stories" minusOff={n <= 0} plusOff={c.left - n < top.step} onMinus={() => setStories((s) => ({ ...s, [c.member_id]: Math.max(0, n - top.step) }))} onPlus={() => setStories((s) => ({ ...s, [c.member_id]: n + top.step }))} />
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-sand-300" role="progressbar" aria-valuemin={0} aria-valuemax={top.max_total} aria-valuenow={total_} aria-label="Stories a year">
                  <div className="flex h-full"><div className="bg-forest-700" style={{ width: `${(Math.min(top.included + c.extra, top.max_total) / top.max_total) * 100}%` }} /><div className="bg-gold-500" style={{ width: `${(n / top.max_total) * 100}%` }} /></div>
                </div>
                <div className="flex justify-between text-xs text-foreground/65"><span>{top.included} included{c.extra ? ` + ${c.extra} added` : ""}{n ? ` + ${n} new` : ""}</span><span>{total_} of {top.max_total} a year</span></div>
              </div>);
          })}
          <p className="text-xs text-foreground/65">Billed yearly. The full amount for the year is charged when you confirm.</p>
        </section>) : null}

      {ai && (
        <section className={`${card} flex flex-wrap items-center justify-between gap-3`} data-testid="addon-ai">
          <div className="min-w-[220px] flex-1"><h3 className="font-semibold text-forest-700">{ai.name}</h3>
            <p className="text-sm text-foreground/70">A pack of {ai.pack_size} more AI tests for {money(ai.prices.once)}, valid to the end of this month. Up to {ai.max_packs} packs a month.</p></div>
          <Stepper value={packs} label="packs" minusOff={packs <= 0} plusOff={packs >= ai.packs_left} onMinus={() => setPacks((p) => Math.max(0, p - 1))} onPlus={() => setPacks((p) => p + 1)} />
        </section>)}

      <section className={`${card} text-sm`} data-testid="addon-live">
        <h3 className="font-semibold text-forest-700">Live courses and Diploma</h3>
        <p className="mt-1 text-foreground/70">Priced per course. The {d.diploma.name} is {money(d.diploma.price)} for {d.diploma.months} months. <a className="font-semibold text-forest-700 underline" href="mailto:operations@hikmahverses.com?subject=Live%20courses">Ask us about live learning</a></p>
      </section>

      {items.length > 0 && (
        <div className="sticky bottom-3 z-10 rounded-2xl border border-forest-700 bg-white p-4 shadow-lg" data-testid="pending-bar">
          <ul className="space-y-1 text-sm">{lines.map(([name, amount, unitText]) => <li key={name} className="flex justify-between gap-3"><span>{name}</span><span className="font-semibold">{money(amount)} <span className="font-normal text-foreground/65">{unitText}</span></span></li>)}</ul>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-sand-300 pt-3">
            <div><div className="text-xs text-foreground/65">Due today</div><div className="font-display text-xl font-semibold text-forest-700" data-testid="due-today">{money(total)}</div></div>
            <button type="button" className={`${btn} bg-forest-700 px-6 text-sand-100 hover:bg-forest-800`} disabled={busy} onClick={confirm} data-testid="confirm-pay">{busy ? "Paying…" : `Confirm and pay ${money(total)}`}</button>
          </div>
          <p className="mt-2 text-xs text-foreground/65">Charged to the card on file. Cancelling an add-on takes effect at the end of the paid period.</p>
        </div>)}
    </div>
  );
}
